/**
 * Shared utilities for React Flow node conversion
 * These utilities are used across different node type converters
 */
import {
  getFieldValueFromHtml,
  getStringFieldValue,
} from '../../shared/fieldMappers'
import type { NodeType } from '../../shared/reactFlowTypes'
import {
  extractTarget,
  type HtmlFieldRecord,
  type NodeDataRecord,
  normalizeChildrenArray,
} from '../../shared/typeUtils'
import type { LegacyFormatNode } from '../index'
import { parsePixelValue } from '../utils'

/**
 * Check if a node kind/type matches the given values
 * @param kind - Legacy node kind string
 * @param nodeType - React Flow node type
 * @param expectedKind - Expected legacy kind string
 * @param expectedNodeType - Expected React Flow node type
 * @returns True if the node matches
 */
export function isNodeType(
  kind: string,
  nodeType: NodeType | 'start',
  expectedKind: string,
  expectedNodeType: NodeType,
): boolean {
  return kind === expectedKind || nodeType === expectedNodeType
}

/**
 * Get field value from HTML object, checking multiple field name variants
 * Re-exported from shared utilities for convenience
 */
export { getFieldValueFromHtml, getStringFieldValue }

/**
 * Normalize children array to ensure it starts with an empty object
 * Re-exported from shared utilities for convenience
 */
export { normalizeChildrenArray, extractTarget }

/**
 * Map a field from HTML to nodeData, checking both snake_case and camelCase variants
 * @param html - HTML object containing legacy fields
 * @param nodeData - Node data object to update
 * @param snakeKey - Snake case field name (e.g., 'on_invalid')
 * @param camelKey - Camel case field name (e.g., 'onInvalid')
 * @param convertNull - If true, convert string "null" to undefined
 */
export function mapField(
  html: HtmlFieldRecord,
  nodeData: NodeDataRecord,
  snakeKey: string,
  camelKey: string,
  convertNull = false,
): void {
  if (snakeKey in html && html[snakeKey]) {
    const value = String(html[snakeKey])
    nodeData[camelKey] = convertNull && value === 'null' ? undefined : value
  } else if (camelKey in html && html[camelKey]) {
    const value = String(html[camelKey])
    nodeData[camelKey] = convertNull && value === 'null' ? undefined : value
  }
}

/**
 * Map onInvalid and onTimeout fields from HTML to nodeData
 * @param html - HTML object containing legacy fields
 * @param nodeData - Node data object to update
 */
export function mapOnInvalidAndOnTimeout(
  html: HtmlFieldRecord,
  nodeData: NodeDataRecord,
): void {
  mapField(html, nodeData, 'on_invalid', 'onInvalid')
  mapField(html, nodeData, 'on_timeout', 'onTimeout')
}

/**
 * Map a field that may be "null" string, converting it to undefined
 * @param html - HTML object containing legacy fields
 * @param nodeData - Node data object to update
 * @param snakeKey - Snake case field name (e.g., 'sound_clip_id')
 * @param camelKey - Camel case field name (e.g., 'soundClipId')
 */
export function mapFieldWithNullConversion(
  html: HtmlFieldRecord,
  nodeData: NodeDataRecord,
  snakeKey: string,
  camelKey: string,
): void {
  if (snakeKey in html && html[snakeKey]) {
    const value = String(html[snakeKey])
    nodeData[camelKey] = value === 'null' ? undefined : value
  } else if (camelKey in html && html[camelKey]) {
    const value = String(html[camelKey])
    nodeData[camelKey] = value === 'null' ? undefined : value
  }
}

/**
 * Parse timeout value from HTML and set it in nodeData if valid
 * @param html - HTML object containing legacy fields
 * @param nodeData - Node data object to update
 * @param timeoutKey - Key to set in nodeData (default: 'timeout')
 */
export function mapTimeout(
  html: HtmlFieldRecord,
  nodeData: NodeDataRecord,
  timeoutKey = 'timeout',
): void {
  if ('timeout' in html && html.timeout) {
    const timeoutValue = parseInt(String(html.timeout), 10)
    if (!isNaN(timeoutValue)) {
      nodeData[timeoutKey] = timeoutValue
    }
  }
}

/**
 * Parse position from legacy node top/left CSS values
 * @param legacyNode - Legacy format node structure
 * @returns Position object with x and y coordinates
 */
export function parseNodePosition(legacyNode: LegacyFormatNode): {
  x: number
  y: number
} {
  return {
    x: parsePixelValue(legacyNode.left),
    y: parsePixelValue(legacyNode.top),
  }
}
